import { useState, type FormEvent } from "react";
import Reveal from "@/components/base/Reveal";

const SUBMIT_URL = "https://readdy.ai/api/form/daf88pptto0ceqqnh7jg";

const INTERESTS = ["情感连麦", "热点吃瓜", "音乐直播", "职场故事", "萌宠治愈", "都想看"];

type Status = "idle" | "submitting" | "success" | "error";

export default function JoinForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);
    const honeypot = ((fd.get("phone_alt") as string) || "").trim();

    // if honeypot filled, silently treat as success (bot)
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const params = new URLSearchParams();
    fd.forEach((value, key) => {
      const v = String(value).trim();
      if (key !== "phone_alt" && v !== "") params.append(key, v);
    });

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, any> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络连接异常，请稍后重试。");
    }
  }

  return (
    <section id="join" className="relative scroll-mt-20 overflow-hidden py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] border border-background-200 bg-gradient-to-br from-primary-500 via-accent-500 to-accent-600 px-6 py-12 md:px-14 md:py-16">
            <div className="pointer-events-none absolute -right-10 -top-16 text-[11rem] text-background-50/10">
              <i className="ri-gift-2-fill"></i>
            </div>
            <div className="pointer-events-none absolute -bottom-14 -left-8 text-[10rem] text-background-50/10">
              <i className="ri-chat-heart-fill"></i>
            </div>

            <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-background-50/20 px-4 py-1.5 text-sm font-medium text-background-50 backdrop-blur">
                  <i className="ri-notification-3-line" />
                  开播提醒 · 粉丝订阅
                </span>
                <h2 className="mt-5 text-2xl md:text-4xl font-black leading-tight text-background-50">
                  订阅开播提醒，
                  <br />
                  喜欢的现场不再错过
                </h2>
                <p className="mt-4 max-w-md text-sm md:text-base leading-7 text-background-50/85">
                  填写邮箱与感兴趣的直播间方向，主播一开播我们第一时间提醒你，精彩回放与福利活动也会同步推送到手。
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {INTERESTS.map((i) => (
                    <span
                      key={i}
                      className="rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1 text-xs font-medium text-background-50"
                    >
                      {i}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl bg-background-50 p-6 md:p-8 shadow-none">
                {status === "success" ? (
                  <div className="flex min-h-[340px] flex-col items-center justify-center text-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-3xl text-accent-800">
                      <i className="ri-check-line"></i>
                    </span>
                    <h3 className="mt-5 text-xl font-bold text-foreground-950">订阅成功，开播见！</h3>
                    <p className="mt-2 text-sm leading-6 text-foreground-600">
                      已把你加入开播提醒名单，主播上麦后我们就会第一时间通知你，记得常来看看哦。
                    </p>
                    <button
                      type="button"
                      onClick={() => setStatus("idle")}
                      className="mt-6 rounded-full bg-secondary-100 px-6 py-2.5 text-sm font-semibold text-foreground-800 transition hover:bg-secondary-200 whitespace-nowrap"
                    >
                      再订阅一个方向
                    </button>
                  </div>
                ) : (
                  <form
                    data-readdy-form="subscribe"
                    id="subscribe-form"
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-4"
                  >
                    <div>
                      <label htmlFor="sub-name" className="mb-1.5 block text-sm font-semibold text-foreground-800">
                        怎么称呼你
                      </label>
                      <input
                        id="sub-name"
                        name="name"
                        type="text"
                        required
                        maxLength={20}
                        placeholder="例如：爱吃瓜的小鹿"
                        className="w-full rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm outline-none transition focus:border-primary-400 focus:ring-4 focus:ring-primary-100"
                      />
                    </div>
                    <div>
                      <label htmlFor="sub-email" className="mb-1.5 block text-sm font-semibold text-foreground-800">
                        常用邮箱
                      </label>
                      <input
                        id="sub-email"
                        name="email"
                        type="email"
                        required
                        placeholder="you@example.com"
                        className="w-full rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm outline-none transition focus:border-primary-400 focus:ring-4 focus:ring-primary-100"
                      />
                    </div>
                    <div>
                      <label htmlFor="sub-interest" className="mb-1.5 block text-sm font-semibold text-foreground-800">
                        最感兴趣的方向
                      </label>
                      <select
                        id="sub-interest"
                        name="interest"
                        defaultValue=""
                        required
                        className="w-full rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm outline-none transition focus:border-primary-400 focus:ring-4 focus:ring-primary-100"
                      >
                        <option value="" disabled>
                          请选择
                        </option>
                        {INTERESTS.map((i) => (
                          <option key={i} value={i}>
                            {i}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* anti-spam honeypot */}
                    <div className="form-hp-field" aria-hidden="true">
                      <label htmlFor="phone_alt">电话（请勿填写）</label>
                      <input
                        id="phone_alt"
                        name="phone_alt"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        readOnly
                      />
                    </div>

                    {status === "error" && (
                      <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600" role="alert">
                        <i className="ri-error-warning-line mr-1"></i>
                        {formError}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 px-6 py-3.5 text-base font-bold text-foreground-950 transition hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap"
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" /> 正在订阅…
                        </>
                      ) : (
                        <>
                          <i className="ri-bell-fill" /> 订阅开播提醒
                        </>
                      )}
                    </button>
                    <p className="text-center text-xs text-foreground-500">
                      提交即代表同意接收开播提醒，可随时退订。
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}